/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from 'react';
import _ from 'underscore';
import classNames from 'classnames';

import {
  Dialog, DialogFooter, DefaultButton, PrimaryButton,
} from '@bingads-webui-react/fabric-control';
import { withLock, keyVault, priorityLevel } from '@bingads-webui-react/popup-manager';
import type { IWithLockOptions } from '@bingads-webui-react/popup-manager';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Scenario, Activity, withActivityContext } from '@bingads-webui-react/hoc-with-signal';

import { withUserGovernance } from './with-user-governance';
import { getStyles } from './styles';
import { useLogger } from './logger';
import type {
  IActivity, ICriticalInfoRawProps, IUserGovernanceConfig,
} from './types';

export type CriticalInfoProps = Omit<
ICriticalInfoRawProps,
'activity' | 'classes' | 'secondaryButtonText'
> & Partial<
Pick<ICriticalInfoRawProps, 'activity' | 'secondaryButtonText'>
& { userGovernanceConfig: IUserGovernanceConfig }
> & Pick<IWithLockOptions, 'force'>;

const DialogWithMinHeight = Dialog as React.ComponentType<
React.ComponentProps<typeof Dialog> & { minHeight?: number }
>;

const CriticalInfoRaw = ({
  activity,
  classes,
  featureName,
  title,
  subTitle,
  content,
  primaryButtonText,
  secondaryButtonText,
  onDone,
  onDismiss,
}: ICriticalInfoRawProps): React.ReactElement => {
  const { logTrace } = useLogger(activity, featureName);

  useEffect(() => {
    logTrace('critical info show');
  }, []);

  const [isDialogOpen, setIsDialogOpen] = useState(true);

  const onPrimaryButtonClick = useCallback(() => {
    logTrace('critical info done');
    setIsDialogOpen(false);
    onDone(true /* skipOnDoneGovernance */);
  }, [logTrace, onDone]);

  const onDialogDismiss = useCallback(() => {
    logTrace('critical info dismiss');
    setIsDialogOpen(false);
    onDismiss();
  }, [logTrace, onDismiss]);

  return isDialogOpen && (
    <DialogWithMinHeight
      className={classNames(['critical-info', classes?.criticalInfo])}
      hidden={false}
      dialogContentProps={{
        title,

        subTitle,
        showCloseButton: false,
      } as React.ComponentProps<typeof Dialog>['dialogContentProps']}
      minWidth={432}
      maxWidth={432}
      minHeight={172}
      modalProps={{
        isBlocking: true,

        dragOptions: false as unknown as NonNullable<
        React.ComponentProps<typeof Dialog>['modalProps']
        >['dragOptions'],
      }}
    >
      {content}
      <DialogFooter
        className={classes?.dialogFooter}
      >
        <PrimaryButton
          onClick={onPrimaryButtonClick}
          text={primaryButtonText}
        />
        <DefaultButton
          onClick={onDialogDismiss}
          text={secondaryButtonText}
        />
      </DialogFooter>
    </DialogWithMinHeight>
  );
};

CriticalInfoRaw.defaultProps = {
  subTitle: '',
  activity: {
    trace: _.noop,
    error: _.noop,
  },
};

const CriticalInfoWithHOC = _.compose(
  withActivityContext,
  withUserGovernance,
  (WrappedComponent: React.ComponentType<ICriticalInfoRawProps>) => withLock(WrappedComponent, {
    lockKey: keyVault.criticalInfo,
    priority: priorityLevel.criticalInfo,
  }),
  (WrappedComponent: React.ComponentType<ICriticalInfoRawProps>) => withDefaultStyles(WrappedComponent, getStyles)
)(CriticalInfoRaw);

export const CriticalInfo = (props: CriticalInfoProps) => (
  <Scenario name="CriticalInfo">
    <Activity name="CriticalInfo">
      <CriticalInfoWithHOC
        {...props}
      />
    </Activity>
  </Scenario>
);
